import { useState, useCallback, useMemo } from 'react';
import type { CampaignData, CollectionKey } from '../../shared/types';

export function useEntityLinks(campaignData: CampaignData) {
  const [expandedCard, setExpandedCard] = useState<{ category: CollectionKey; id: number } | null>(null);

  const normalizeValue = (value: string) => value.trim().toLowerCase();

  const wikiLinksByCategory = useMemo(() => {
    const base = {
      npcs: new Map<number, string[]>(),
      locations: new Map<number, string[]>(),
      organizations: new Map<number, string[]>(),
      plotThreads: new Map<number, string[]>(),
    };

    for (const article of campaignData.wikiArticles || []) {
      if (!article.entityLinks) continue;
      for (const link of article.entityLinks) {
        const bucket = base[link.category];
        if (!bucket) continue;
        const existing = bucket.get(link.itemId) ?? [];
        existing.push(article.title);
        bucket.set(link.itemId, existing);
      }
    }

    for (const bucket of Object.values(base)) {
      for (const [key, titles] of bucket.entries()) {
        const unique = Array.from(new Set(titles));
        bucket.set(key, unique);
      }
    }

    return base;
  }, [campaignData.wikiArticles]);

  const resolveLocationEntity = useCallback((name: string) => {
    const target = normalizeValue(name);
    return (campaignData.locations || []).find(
      (loc) => normalizeValue(loc.name) === target
    );
  }, [campaignData.locations]);

  const resolveLocation = useCallback((name: string) => {
    const match = resolveLocationEntity(name);
    return match ? match.name : null;
  }, [resolveLocationEntity]);

  const resolveOrganization = useCallback((name: string) => {
    const target = normalizeValue(name);
    return (campaignData.organizations || []).find(
      (org) => normalizeValue(org.name) === target
    );
  }, [campaignData.organizations]);

  const resolveNpc = useCallback((name: string) => {
    const target = normalizeValue(name);
    return (campaignData.npcs || []).find(
      (npc) => normalizeValue(npc.name) === target
    );
  }, [campaignData.npcs]);

  const resolveItemLinks = useCallback((fieldKey: string, value: string, item?: Record<string, unknown>) => {
    if (!value) return [];
    if (fieldKey === 'location') {
      const locationId = typeof item?.locationId === 'number' ? item.locationId : null;
      if (locationId) {
        const match = campaignData.locations?.find((loc) => loc.id === locationId);
        if (match) return [{ tab: 'locations', query: match.name }];
      }
      const resolved = resolveLocation(value);
      return resolved ? [{ tab: 'locations', query: resolved }] : [];
    }
    if (fieldKey === 'connections') {
      const parts = value.split(',').map((part) => part.trim()).filter(Boolean);
      const links = parts
        .map((part) => resolveLocation(part))
        .filter((resolved): resolved is string => Boolean(resolved))
        .map((resolved) => ({ tab: 'locations', query: resolved }));
      return links;
    }
    if (fieldKey === 'associatedNpcs') {
      const locId = typeof item?.id === 'number' ? item.id : null;
      const locName = typeof item?.name === 'string' ? item.name : '';
      const matches = (campaignData.npcs || []).filter((npc) => {
        if (locId && npc.locationId === locId) return true;
        if (locName && normalizeValue(npc.location) === normalizeValue(locName)) return true;
        return false;
      });
      return matches.map((npc) => ({ tab: 'npcs', query: npc.name }));
    }
    if (fieldKey === 'wikiLinks') {
      const rawLinks = Array.isArray(item?.wikiLinks)
        ? item?.wikiLinks
        : value.split(',').map((part) => part.trim()).filter(Boolean);
      return (rawLinks || []).map((title) => ({ tab: 'wiki', query: title }));
    }
    return [];
  }, [campaignData.locations, campaignData.npcs, resolveLocation]);

  const getNpcNamesForLocation = useCallback((location: { id: number; name: string }) => {
    const associatedById = (campaignData.npcs || [])
      .filter((npc) => npc.locationId === location.id)
      .map((npc) => npc.name)
      .filter(Boolean);
    const associatedByName = (campaignData.npcs || [])
      .filter(
        (npc) =>
          npc.location && normalizeValue(npc.location) === normalizeValue(location.name)
      )
      .map((npc) => npc.name)
      .filter(Boolean);
    return Array.from(new Set([...associatedById, ...associatedByName])).join(', ') || '—';
  }, [campaignData.npcs]);

  const openOrganizationCard = useCallback((factionName: string) => {
    const match = resolveOrganization(factionName);
    if (match) {
      setExpandedCard({ category: 'organizations', id: match.id });
    }
  }, [resolveOrganization]);

  const openLocationCard = useCallback((locationName: string) => {
    const match = resolveLocationEntity(locationName);
    if (match) {
      setExpandedCard({ category: 'locations', id: match.id });
    }
  }, [resolveLocationEntity]);

  const openNpcCard = useCallback((npcName: string) => {
    const match = resolveNpc(npcName);
    if (match) {
      setExpandedCard({ category: 'npcs', id: match.id });
    }
  }, [resolveNpc]);

  const openEntityLink = useCallback((tab: string, query?: string) => {
    if (!query) return;
    if (tab === 'locations') {
      openLocationCard(query);
    } else if (tab === 'npcs') {
      openNpcCard(query);
    }
  }, [openLocationCard, openNpcCard]);

  const isCardExpanded = useCallback((category: CollectionKey, id: number) =>
    expandedCard?.category === category && expandedCard.id === id,
  [expandedCard]);

  const handleExpandedChange = useCallback(
    (category: CollectionKey, id: number) => (expanded: boolean) =>
      setExpandedCard(expanded ? { category, id } : null),
    []
  );

  const expandedOrganization =
    expandedCard?.category === 'organizations'
      ? campaignData.organizations?.find((org) => org.id === expandedCard.id) || null
      : null;

  const expandedLocation =
    expandedCard?.category === 'locations'
      ? campaignData.locations?.find((loc) => loc.id === expandedCard.id) || null
      : null;

  const expandedNpc =
    expandedCard?.category === 'npcs'
      ? campaignData.npcs?.find((npc) => npc.id === expandedCard.id) || null
      : null;

  const getWikiLinksForItem = useCallback(
    (category: 'npcs' | 'locations' | 'organizations' | 'plotThreads', id: number) =>
      wikiLinksByCategory[category]?.get(id) ?? [],
    [wikiLinksByCategory]
  );

  return {
    expandedCard,
    setExpandedCard,
    resolveItemLinks,
    getNpcNamesForLocation,
    openOrganizationCard,
    openLocationCard,
    openNpcCard,
    openEntityLink,
    isCardExpanded,
    handleExpandedChange,
    expandedOrganization,
    expandedLocation,
    expandedNpc,
    getWikiLinksForItem,
  };
}
